import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { 
  MessageCircle, Search, Send, Phone, Video, MoreVertical, 
  Paperclip, Smile, Mic, User, Check, CheckCheck, Clock,
  Filter, Bot, UserCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppSupport() {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Sample conversations
  const sampleConversations = [
    { 
      id: 1, 
      name: 'John Smith', 
      phone: '+1 555-0123',
      lastMessage: 'My software license is not working',
      time: '10:30 AM',
      unread: 2,
      status: 'open',
      avatar: 'J',
      isAI: false
    },
    { 
      id: 2, 
      name: 'Sarah Johnson', 
      phone: '+1 555-0456',
      lastMessage: 'Thank you for the quick response!',
      time: '9:45 AM',
      unread: 0,
      status: 'resolved',
      avatar: 'S',
      isAI: true
    },
    { 
      id: 3, 
      name: 'Mike Brown', 
      phone: '+1 555-0789',
      lastMessage: 'When will my AMC expire?',
      time: 'Yesterday',
      unread: 1,
      status: 'pending',
      avatar: 'M',
      isAI: false
    },
    { 
      id: 4, 
      name: 'Emily Davis', 
      phone: '+1 555-0321',
      lastMessage: 'I need help with installation',
      time: 'Yesterday',
      unread: 3,
      status: 'escalated',
      avatar: 'E',
      isAI: false
    },
    { 
      id: 5, 
      name: 'David Wilson', 
      phone: '+1 555-0654',
      lastMessage: 'Got it, thanks!',
      time: '2 days ago',
      unread: 0,
      status: 'closed',
      avatar: 'D',
      isAI: true
    },
  ];

  const sampleMessages = [
    { id: 1, sender: 'customer', content: 'Hi, I purchased the software yesterday', time: '10:15 AM', status: 'read' },
    { id: 2, sender: 'ai', content: 'Hello John! I\'m the AI Support Assistant. I can help you with your software purchase. What seems to be the issue?', time: '10:16 AM', status: 'read' },
    { id: 3, sender: 'customer', content: 'My software license is not working. It says "License validation failed"', time: '10:20 AM', status: 'read' },
    { id: 4, sender: 'ai', content: 'I understand. This error usually occurs due to:\n\n1. Network connectivity issues\n2. Firewall blocking the license server\n3. Incorrect license key entry\n\nLet me check your license details. Could you please share your order number?', time: '10:22 AM', status: 'read' },
    { id: 5, sender: 'customer', content: 'Order number is ORD-2025-1234', time: '10:25 AM', status: 'read' },
    { id: 6, sender: 'ai', content: 'Thank you! I found your order. Your license appears to be valid. Let me guide you through the troubleshooting steps.\n\nFirst, please try:\n• Restart your computer\n• Disable firewall temporarily\n• Run the software as administrator\n\nDoes this help?', time: '10:27 AM', status: 'read' },
    { id: 7, sender: 'customer', content: 'Still not working. Can I speak to a human agent?', time: '10:30 AM', status: 'delivered' },
  ];

  const displayConversations = conversations.length > 0 ? conversations : sampleConversations;

  const filteredConversations = displayConversations.filter(conv => {
    const matchesSearch = conv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          conv.phone.includes(searchTerm);
    const matchesFilter = filterType === 'all' || 
                          (filterType === 'ai' && conv.isAI) ||
                          (filterType === 'agent' && !conv.isAI);
    return matchesSearch && matchesFilter;
  });

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setMessages(sampleMessages);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      // Add message to local state immediately
      const newMsg = {
        id: messages.length + 1,
        sender: 'agent',
        content: newMessage,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'sending'
      };
      setMessages([...messages, newMsg]);
      setNewMessage('');
      
      // Simulate API call
      setTimeout(() => {
        setMessages(prev => prev.map(m => 
          m.id === newMsg.id ? { ...m, status: 'delivered' } : m
        ));
      }, 1000);
      
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-blue-500',
      'pending': 'bg-amber-500',
      'resolved': 'bg-emerald-500',
      'escalated': 'bg-red-500',
      'closed': 'bg-zinc-500'
    };
    return colors[status] || 'bg-zinc-500';
  };

  const getMessageStatus = (status) => {
    if (status === 'read') return <CheckCheck className="w-4 h-4 text-blue-400" />;
    if (status === 'delivered') return <CheckCheck className="w-4 h-4 text-muted-foreground" />;
    if (status === 'sending') return <Clock className="w-3 h-3 text-muted-foreground" />;
    return <Check className="w-4 h-4 text-muted-foreground" />;
  };

  return (
    <div className="h-[calc(100vh-140px)] flex gap-6 animate-fade-in">
      {/* Conversations List */}
      <div className="w-80 flex flex-col support-card p-0 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold flex items-center gap-2 mb-3">
            <MessageCircle className="w-5 h-5 text-primary" />
            WhatsApp Support
          </h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="support-input pl-10 text-sm"
              data-testid="conversation-search"
            />
          </div>
          <div className="flex gap-2 mt-3">
            {['all', 'ai', 'agent'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                  filterType === type 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary hover:bg-secondary/80'
                }`}
              >
                {type === 'all' ? 'All' : type === 'ai' ? 'AI Handled' : 'Agent'}
              </button>
            ))}
          </div>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.map((conv) => (
            <div
              key={conv.id}
              onClick={() => handleSelectConversation(conv)}
              className={`p-4 border-b border-border/50 cursor-pointer transition-colors ${
                selectedConversation?.id === conv.id ? 'bg-primary/10' : 'hover:bg-secondary/50'
              }`}
              data-testid={`conversation-${conv.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium">
                    {conv.avatar}
                  </div>
                  {conv.isAI && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                      <Bot className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm truncate">{conv.name}</span>
                    <span className="text-xs text-muted-foreground">{conv.time}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{conv.lastMessage}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`w-2 h-2 rounded-full ${getStatusColor(conv.status)}`}></span>
                    <span className="text-xs text-muted-foreground capitalize">{conv.status}</span>
                    {conv.unread > 0 && (
                      <span className="ml-auto bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                        {conv.unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      {selectedConversation ? (
        <div className="flex-1 flex flex-col support-card p-0 overflow-hidden">
          {/* Chat Header */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium">
                {selectedConversation.avatar}
              </div>
              <div>
                <h3 className="font-medium">{selectedConversation.name}</h3>
                <p className="text-xs text-muted-foreground">{selectedConversation.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-secondary rounded-lg transition-colors">
                <Phone className="w-5 h-5 text-muted-foreground" />
              </button>
              <button className="p-2 hover:bg-secondary rounded-lg transition-colors">
                <Video className="w-5 h-5 text-muted-foreground" />
              </button>
              <button className="p-2 hover:bg-secondary rounded-lg transition-colors">
                <MoreVertical className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === 'customer' ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`max-w-[70%] ${
                  msg.sender === 'customer' 
                    ? 'chat-bubble-user' 
                    : msg.sender === 'ai' 
                      ? 'chat-bubble-ai' 
                      : 'chat-bubble-agent'
                }`}>
                  {msg.sender !== 'customer' && (
                    <div className="flex items-center gap-2 mb-1 text-xs text-muted-foreground">
                      {msg.sender === 'ai' ? (
                        <>
                          <Bot className="w-3 h-3" />
                          <span>AI Assistant</span>
                        </>
                      ) : (
                        <>
                          <UserCircle className="w-3 h-3" />
                          <span>You</span>
                        </>
                      )}
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-line">{msg.content}</p>
                  <div className="flex items-center justify-end gap-1 mt-1">
                    <span className="text-xs text-muted-foreground">{msg.time}</span>
                    {msg.sender !== 'customer' && getMessageStatus(msg.status)}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* AI Banner */}
          {selectedConversation.isAI && (
            <div className="mx-4 mb-2 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg flex items-center gap-3">
              <Bot className="w-5 h-5 text-violet-400" />
              <p className="text-sm text-violet-300">This conversation is being handled by AI. Click to take over manually.</p>
              <button className="ml-auto support-btn-secondary text-xs px-3 py-1">
                Take Over
              </button>
            </div>
          )}

          {/* Message Input */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-secondary rounded-lg transition-colors">
                <Paperclip className="w-5 h-5 text-muted-foreground" />
              </button>
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type a message..."
                  className="support-input pr-20"
                  data-testid="message-input"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button className="p-1.5 hover:bg-secondary rounded transition-colors">
                    <Smile className="w-5 h-5 text-muted-foreground" />
                  </button>
                  <button className="p-1.5 hover:bg-secondary rounded transition-colors">
                    <Mic className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                className="support-btn-primary p-3"
                data-testid="send-message-btn"
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 support-card flex items-center justify-center">
          <div className="text-center">
            <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Select a Conversation</h3>
            <p className="text-muted-foreground text-sm">Choose a conversation from the list to view messages</p>
          </div>
        </div>
      )}
    </div>
  );
}
